import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { loadAccountHistory, type AccountHistoryPage } from "./account-history";
import { accountHistoryUrl, hierarchyUrl } from "./urls";

export function AccountHistoryBrowser({ accountId }: { accountId: string }) {
  const pending = useRef<AbortController | undefined>(undefined);
  const [state, setState] = useState<{ accountId: string; page?: AccountHistoryPage; failed?: boolean }>();
  function read(previous?: AccountHistoryPage) {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setState(undefined);
    void loadAccountHistory(accountId, previous, controller.signal).then(
      (page) => { if (!controller.signal.aborted) setState({ accountId, page }); },
      () => { if (!controller.signal.aborted) setState({ accountId, failed: true }); },
    );
  }
  useEffect(() => {
    read();
    const refresh = () => read();
    window.addEventListener("focus", refresh);
    return () => { pending.current?.abort(); window.removeEventListener("focus", refresh); };
  }, [accountId]);
  const current = state?.accountId === accountId ? state : undefined;
  return <main className="mx-auto w-full max-w-3xl px-4 py-8 sm:px-8 sm:py-12 text-[#20272b]">
    <a className="text-sm text-[#52606e] underline" href={hierarchyUrl(accountId)}>Workspaces</a>
    <h1 className="mt-5 text-2xl font-semibold tracking-[-0.04em]">Director history</h1>
    {!current ? <p role="status" className="mt-6 text-sm text-[#52606e]">Loading history…</p> : current.failed || !current.page ? <>
      <p role="status" className="mt-6 text-sm text-[#52606e]">History is unavailable. Check your account or try again when you're connected.</p>
      <Button className="mt-4" onClick={() => read()}>Try again</Button>
    </> : <>
      {current.page.threads.length ? <ul className="mt-8 divide-y divide-[#cfe4f1] border-y border-[#cfe4f1]">
        {current.page.threads.map((thread) => <li key={thread.id}><a className="block rounded-md px-2 py-4 hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7376a8]" href={accountHistoryUrl(accountId, thread.id)}>{thread.name}</a></li>)}
      </ul> : <p className="mt-6 text-sm text-[#52606e]">No earlier Director conversations.</p>}
      {current.page.nextCursor ? <Button className="mt-6" variant="ghost" onClick={() => read(current.page)}>Load earlier conversations</Button> : null}
    </>}
  </main>;
}
